import { useMemo } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import { MultiSelect, CardCheckbox, Tooltip, FieldData, allCardBrands } from 'vopay';

import { PaymentFieldDefault } from './types';

export function useElinxCardFields(): FieldData<PaymentFieldDefault>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'UseTransactionCreationFields.CardSettings' });

  return useMemo(() => {
    return [
      {
        label: () => t('acceptedCardBrands.label', { defaultValue: 'Accepted Card Brands' }),
        fieldName: 'AcceptedCardBrands',
        getInputState: () => 'required',
        showField: (values) => {
          const paymentMethods = values.PaymentSelectionMethod?.split(',') || [];
          return (
            (paymentMethods.includes('credit') || paymentMethods.includes('debitcard')) &&
            values.ProvideOrRequestPaymentInformation === 'request'
          );
        },
        tooltip: () => (
          <Tooltip
            information={t('acceptedCardBrands.tooltip', {
              defaultValue: 'Select the card brands this contact should be able to provide.',
            })}
            position="top"
            width="394px"
          />
        ),
        serializeField: (values) => {
          const selectedBrands = values.AcceptedCardBrands?.split(',').filter((b) => b) || [];
          if (selectedBrands.length === allCardBrands.length) {
            return '';
          }
          return selectedBrands.join(',');
        },
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string[], string>({
            formatValue: (value) => (value ? value.split(',').filter((b) => b) : []),
          });

          return (
            <MultiSelect
              placeholder={t('acceptedCardBrands.placeholder', { defaultValue: 'Select Card Brands' })}
              options={allCardBrands.map((brand) => ({
                label: brand.charAt(0).toUpperCase() + brand.slice(1),
                value: brand,
              }))}
              {...fieldData}
            />
          );
        },
        validate: (ctx) => {
          const paymentMethods = ctx.values.PaymentSelectionMethod?.split(',') || [];
          return (paymentMethods.includes('credit') || paymentMethods.includes('debitcard')) &&
            ctx.values.ProvideOrRequestPaymentInformation === 'request'
            ? Yup.string().required()
            : Yup.string();
        },
      },
      {
        label: () => t('cardTypeValidation.label', { defaultValue: 'Card Type Validation' }),
        fieldName: 'CardTypeValidation',
        getInputState: () => 'optional',
        showField: (values) => {
          const paymentMethods = values.PaymentSelectionMethod?.split(',') || [];
          return (
            (paymentMethods.includes('credit') || paymentMethods.includes('debitcard')) &&
            values.ProvideOrRequestPaymentInformation === 'request'
          );
        },
        tooltip: () => (
          <Tooltip
            information={t('cardTypeValidation.tooltip', {
              defaultValue:
                "Validates whether the entered card is debit or credit and blocks cards that don't match the allowed types.",
            })}
            position="top"
            width="394px"
          />
        ),
        renderField: ({ getFormikData, ctx }) => {
          const fieldData = getFormikData<boolean>({
            formatValue: (value) => !!value,
          });
          return (
            <CardCheckbox
              label={t('cardTypeValidation.checkboxLabel', { defaultValue: 'Enable card type validation' })}
              checked={ctx.values.CardTypeValidation || false}
              {...fieldData}
            />
          );
        },
        validate: () => Yup.boolean(),
      },
    ];
  }, [t]);
}
